<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
      <div>
        <h4 class="fw-bold text-black mb-0">
          <span v-if="currentQueueName">
            <i class="flaticon-folder me-2"></i>
            {{ currentQueueName }}
          </span>
          <span v-else>
            Mes Dossiers à Traiter
          </span>
        </h4>
        <small v-if="currentQueueName" class="text-muted">
          {{ totalElements }} dossier(s) dans cette file de validation
        </small>
      </div>
      <div class="d-flex align-items-center gap-2">
        <!-- Breadcrumb si on vient d'une file spécifique -->
        <router-link 
          v-if="selectedQueue" 
          to="/agent-dashboard" 
          class="btn btn-outline-secondary btn-sm me-2"
        >
          <i class="flaticon-arrow-left me-1"></i> Retour au dashboard
        </router-link>
        
        <!-- Filtre par file si on n'est pas dans une file spécifique -->
        <select 
          v-if="!selectedQueue" 
          v-model="selectedQueue" 
          @change="loadBescs(1, limit)" 
          class="form-select form-select-sm" 
          style="width: auto;"
        >
          <option value="">Toutes les files</option>
          <option v-for="queue in assignedQueues" :key="queue.id" :value="queue.id">
            {{ queue.name }}
          </option>
        </select>

        <!-- Filtre par statut -->
        <select 
          v-model="selectedStatus" 
          @change="loadBescs(1, limit)" 
          class="form-select form-select-sm" 
          style="width: auto;"
        >
          <option value="">Tous les statuts</option>
          <option value="PENDING">En attente</option>
          <option value="VALIDATED">Validés</option>
          <option value="REJECTED">Rejetés</option>
        </select>
        
        <!-- Recherche -->
        <form class="search-box position-relative me-15" @submit.prevent="rechercher">
          <input 
            type="text" 
            name="mot" 
            v-model="searchTerm" 
            @input="rechercher"
            class="form-control shadow-none text-black rounded-0 border-0"
            placeholder="Rechercher..." 
          />
          <button type="submit" class="bg-transparent text-primary transition p-0 border-0">
            <i class="flaticon-search-interface-symbol"></i>
          </button>
        </form>

        <button @click="loadBescs(1, limit)" class="btn btn-primary btn-sm">
          <i class="flaticon-refresh me-1"></i> Actualiser
        </button>
      </div>
    </div>

    <div class="card-body p-15 p-sm-20 p-md-25">
      <!-- Table des BESC -->
      <div class="table-responsive">
        <table class="table text-nowrap align-middle mb-0">
          <thead>
            <tr>
              <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Référence BESC
              </th>
              <th v-if="!selectedQueue" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                File
              </th>
              <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Importateur
              </th>
              <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Port déchargement
              </th>
              <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Statut
              </th>
              <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Niveau risque
              </th>
              <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Créé le
              </th>
              <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end pe-0">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td :colspan="selectedQueue ? 7 : 8" class="text-center py-2">
                <span class="text-muted">Chargement...</span>
              </td>
            </tr>
            <tr v-else-if="bescs.length === 0">
              <td :colspan="selectedQueue ? 7 : 8" class="text-center py-4">
                <i class="flaticon-folder-open text-muted" style="font-size: 48px;"></i>
                <p class="text-muted mt-2">
                  <span v-if="searchTerm">
                    Aucun dossier ne correspond à votre recherche "{{ searchTerm }}"
                  </span>
                  <span v-else-if="selectedStatus">
                    Aucun dossier avec le statut "{{ getStatusLabel(selectedStatus) }}"
                  </span>
                  <span v-else>
                    Aucun dossier trouvé dans cette file
                  </span>
                </p>
              </td>
            </tr>
            <tr v-else v-for="besc in bescs" :key="besc.id">
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <div class="fw-bold text-primary">{{ besc.refbesc }}</div>
                <div class="small text-muted">{{ besc.numeroConnaissement }}</div>
              </td>
              <td v-if="!selectedQueue" class="shadow-none lh-1 fw-medium text-black-emphasis">
                <div>{{ besc.queue?.name || 'N/A' }}</div>
                <div class="small text-muted">{{ besc.queue?.code || '' }}</div>
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ besc.importateur || '-' }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ besc.portDechargement?.nomPort || '-' }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <span class="badge" :class="getStatusBadgeClass(besc.currentStatus?.code)">
                  {{ besc.currentStatus?.label || 'Inconnu' }}
                </span>
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <span v-if="besc.riskLevel" class="badge" :class="getRiskBadgeClass(besc.riskLevel)">
                  {{ getRiskLabel(besc.riskLevel) }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ format_date(besc.createdAt) }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end pe-0">
                <router-link
                  :to="{ name: 'ViewBesc', params: { id: besc.id }, query: { mode: 'traitement' } }"
                  class="btn btn-success btn-sm"
                  title="Traiter ce dossier"
                >
                  <i class="flaticon-check me-1"></i>
                  Traiter
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination-area d-md-flex mt-15 mt-sm-20 mt-md-25 justify-content-between align-items-center">
        <PaginationComponent 
          :page="page" 
          :totalPages="totalPages" 
          :totalElements="totalElements" 
          :limit="limit"
          @paginate="handlePaginate" 
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import ApiService from '@/services/ApiService';
import JwtService from '@/services/JwtService';
import { error, format_date } from '@/utils/utils';
import Multiselect from '@vueform/multiselect'
import PaginationComponent from '@/components/Utilities/Pagination.vue';

interface Besc {
  id: string;
  refbesc: string;
  numeroConnaissement: string;
  importateur: string;
  portDechargement: {
    nomPort: string;
  };
  currentStatus: {
    code: string;
    label: string;
  };
  riskLevel: string;
  createdAt: string;
  queue: {
    name: string;
    code: string;
  };
}

interface ValidationQueue {
  id: string;
  name: string;
  code: string;
}

export default defineComponent({
  name: 'AgentFiles',
  components: {
    PaginationComponent,
  },
  setup() {
    const route = useRoute();
    const loading = ref(false);
    const bescs = ref<Besc[]>([]);
    const assignedQueues = ref<ValidationQueue[]>([]);
    const selectedQueue = ref(route.query.queueId as string || '');
    const selectedStatus = ref('');
    const currentQueueName = ref('');
    const searchTerm = ref('');

    const page = ref(1);
    const totalPages = ref(0);
    const limit = ref(25);
    const totalElements = ref(0);

    // Timer pour le debounce de la recherche
    let searchTimeout: ReturnType<typeof setTimeout> | null = null;

    const loadBescs = async (pageNum = 1, limitNum = 25) => {
      loading.value = true;
      try {
        const user = JwtService.getUser();
        if (!user) {
          error("Utilisateur non connecté");
          return;
        }

        let endpoint = '';
        let response;

        if (selectedQueue.value) {
          const params = new URLSearchParams({
            page: pageNum.toString(),
            limit: limitNum.toString(),
            userCreationId: user.userId
          });

          if (searchTerm.value) {
            params.append('mot', searchTerm.value);
          }

          if (selectedStatus.value) {
            params.append('status', selectedStatus.value);
          }

          endpoint = `/bescs/queue/${selectedQueue.value}?${params}`;
          response = await ApiService.get(endpoint);
        } else {
          endpoint = '/bescs';
          response = await ApiService.post(endpoint, {
            page: pageNum,
            limit: limitNum,
            mot: searchTerm.value || undefined,
            status: selectedStatus.value || undefined,
            userCreationId: user.userId
          });
        }
        
        if (response.data.code === 200) {
          bescs.value = response.data.data.data || [];
          totalPages.value = response.data.data.totalPages || 0;
          totalElements.value = response.data.data.totalElements || 0;
          page.value = pageNum;
          limit.value = limitNum;
        }

      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du chargement des dossiers');
      } finally {
        loading.value = false;
      }
    };

    // Fonction de recherche avec debounce
    const rechercher = () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      searchTimeout = setTimeout(() => {
        page.value = 1;
        loadBescs(1, limit.value);
      }, 500);
    };

    const loadAssignedQueues = async () => {
      try {
        const user = JwtService.getUser();
        if (!user) return;

        const response = await ApiService.get(`/agent/queues/${user.userId}`);
        assignedQueues.value = response.data.data;
        
        if (selectedQueue.value) {
          const currentQueue = assignedQueues.value.find(q => q.id === selectedQueue.value);
          currentQueueName.value = currentQueue?.name || '';
        }
      } catch (err: any) {
        console.error('Erreur chargement files:', err);
      }
    };

    const handlePaginate = ({ page_, limit_ }) => {
      loadBescs(page_, limit_);
    };

    const getStatusBadgeClass = (statusCode: string): string => {
      const classes: Record<string, string> = {
        'DRAFT': 'bg-secondary',
        'SUBMITTED': 'bg-info',
        'PENDING': 'bg-warning',
        'VALIDATED': 'bg-success',
        'REJECTED': 'bg-danger',
        'CANCELLED': 'bg-dark'
      };
      return classes[statusCode] || 'bg-secondary';
    };

    const getRiskBadgeClass = (riskLevel: string): string => {
      const classes: Record<string, string> = {
        'HIGH': 'bg-danger',
        'MEDIUM': 'bg-warning',
        'LOW': 'bg-success'
      };
      return classes[riskLevel] || 'bg-secondary';
    };

    const getRiskLabel = (riskLevel: string): string => {
      const labels: Record<string, string> = {
        'HIGH': 'Élevé',
        'MEDIUM': 'Moyen',
        'LOW': 'Faible'
      };
      return labels[riskLevel] || riskLevel;
    };

    const getStatusLabel = (statusCode: string): string => {
      const labels: Record<string, string> = {
        'PENDING': 'En attente',
        'VALIDATED': 'Validés',
        'REJECTED': 'Rejetés'
      };
      return labels[statusCode] || statusCode;
    };

    
    onMounted(() => {
      loadAssignedQueues();
      loadBescs();
    });

    return {
      loading,
      bescs,
      assignedQueues,
      selectedQueue,
      selectedStatus,
      currentQueueName,
      searchTerm,
      page,
      totalPages,
      limit,
      totalElements,
      loadBescs,
      rechercher,
      handlePaginate,
      getStatusBadgeClass,
      getRiskBadgeClass,
      getRiskLabel,
      getStatusLabel,
      format_date
    };
  }
});
</script>

<style scoped>
.table-info {
  background-color: rgba(13, 202, 240, 0.1);
}

.table-success {
  background-color: rgba(25, 135, 84, 0.1);
}

.table-warning {
  background-color: rgba(255, 193, 7, 0.1);
}
</style>